import React, { useEffect, useMemo, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import moment from 'moment';
import { cloneDeep, mergeWith, isEqual } from 'lodash-es';
import usePCConsoleGenreOptionsMap from './usePCConsoleGenreOptionsMap';
import type { OptionsItemType } from '@home/components/NewRegionSelect/type';
import type { IGenreSourcesGenreOptions } from '@intelligence/components/GenreSelect/types';
import { containsAmpereMetric } from '@intelligence/utils/filterUtils';
import { getPcConsoleTopChartsFilterTips } from '@intelligence/pages/TopCharts/PCConsoleGames/_config/pcConsoleTopChartsFilterTips';

type FilterKeyMapSubset = Pick<
  typeof import('@intelligence/components/Filter/FilterBase/_config'),
  'RANGE_TIME' | 'RELEASE_TIME' | 'REGION' | 'PUBLISHER_REGION' | 'PCCONSOLE_TOPCHARTS_METRICS' | 'ALL_Games' | 'STEAM_TAGS' | 'GAME_MODE' | 'PLATFORMCASCADER'
>;

const DEFAULT_REGION_MARKETS = ['global'];

interface UsePcConsoleFiltersParams {
  pageId: string;
  entityType: string;
  filterConfigCur: any;
  initialMetricSelectorValues: string[];
  rangeTimeComponentDefault: any;
  defaultParams: any;
  configureTexts: Record<string, string>;
  regionOptions: OptionsItemType[];
  steamTagOptions: OptionsItemType[];
  gameModeOptions: OptionsItemType[];
  gameEngineOptions: OptionsItemType[];
  publisherClassOptions: OptionsItemType[];
  hasUrlShortLink: boolean;
  filterKeyMap: FilterKeyMapSubset;
  FilterTimeType: any;
  formatMessage: (descriptor: { id: string }) => string;
  userManuallyChangedRegionRef: { current: boolean };
  hasMetricSelectorChangedRef: { current: boolean };
  /** 热门标签选项列表 */
  hotTagsOptions?: any[];
  /** URL filter_tag_name 参数 */
  filterTagName?: string;
  /** 默认匹配到的 hotTag option */
  hotTagDefaultOption?: any;
  /** HotTagsSource 选项变化回调 */
  onHotTagConfirm?: (option: any) => void;
}

export interface UsePcConsoleFiltersResult {
  filterValue: any;
  setFilterValue: (value: any) => void;
  filterPageValue: any;
  setFilterPageValue: (value: any) => void;
  filterCfg: any;
  formatFilterValuesFromUrl: (decoded: any, current: any) => any;
  getRadioOptionsTips: () => Record<string, string>;
  genreOptionsMapLoading: boolean;
  formatedGenreOptionsMap: IGenreSourcesGenreOptions;
}

const usePcConsoleFilters = (params: UsePcConsoleFiltersParams): UsePcConsoleFiltersResult => {
  const {
    pageId,
    entityType,
    filterConfigCur,
    initialMetricSelectorValues,
    rangeTimeComponentDefault,
    defaultParams,
    configureTexts,
    regionOptions,
    steamTagOptions,
    gameModeOptions,
    gameEngineOptions,
    publisherClassOptions,
    hasUrlShortLink,
    filterKeyMap,
    FilterTimeType,
    formatMessage,
    userManuallyChangedRegionRef,
    hasMetricSelectorChangedRef,
    hotTagsOptions,
    filterTagName,
    hotTagDefaultOption,
    onHotTagConfirm,
  } = params;

  const rangeTimeDefaultConfig = filterConfigCur?.defaultFilterValue?.[filterKeyMap.RANGE_TIME];

  const {
    loading: genreOptionsMapLoading,
    map: genreOptionsMap,
    formatedMap: formatedGenreOptionsMap,
  } = usePCConsoleGenreOptionsMap(entityType);

  const rangeTimeResetValue = useMemo(() => {
    if (rangeTimeDefaultConfig?.type === 'date') {
      const start = (defaultParams as any)?.start_date;
      const end = (defaultParams as any)?.end_date;
      if (start && end) {
        return [moment(start), moment(end)];
      }
      if (Array.isArray(rangeTimeComponentDefault?.date)) {
        return rangeTimeComponentDefault?.date;
      }
      return ['', ''];
    }
    if (rangeTimeDefaultConfig?.type === 'month' && Array.isArray(rangeTimeComponentDefault?.month)) {
      return rangeTimeComponentDefault?.month;
    }
    return rangeTimeComponentDefault?.week;
  }, [defaultParams, rangeTimeComponentDefault, rangeTimeDefaultConfig]);

  const initialFilterValue = useMemo(
    () => ({
      ...filterConfigCur?.defaultFilterValue,
      pcConsole_topCharts_metrics: {
        value: initialMetricSelectorValues,
      },
    }),
    [filterConfigCur?.defaultFilterValue, initialMetricSelectorValues],
  );

  const [filterValue, setFilterValue] = useState<any>(initialFilterValue);
  const [filterPageValue, setFilterPageValue] = useState<any>(initialFilterValue);

  // 当前所选指标是否包含 Ampere 数据源；任一指标命中即视为含 Ampere
  const currentMetricValues = filterValue?.[filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS]?.value;
  const containsAmpere = useMemo(
    () => containsAmpereMetric(currentMetricValues),
    [currentMetricValues],
  );

  // 兜底：当指标从含 Ampere 切到不含 Ampere 时，若当前已选 'global_wo_china'，
  // 由于 RegionSelector 内部 useEffect 未监听 disableNoChina，需在此显式重置 markets。
  useEffect(() => {
    if (containsAmpere) return;
    const region = filterValue?.[filterKeyMap.REGION];
    if (region?.markets?.[0] !== 'global_wo_china') return;
    const nextRegion = {
      ...region,
      markets: DEFAULT_REGION_MARKETS,
      regions: [],
      type: 'market',
    };
    const nextFilterValue = { ...filterValue, [filterKeyMap.REGION]: nextRegion };
    setFilterValue(nextFilterValue);
    setFilterPageValue(nextFilterValue);
    userManuallyChangedRegionRef.current = true;
  }, [containsAmpere]);

  const overwriteArrayCustomizer = useMemoizedFn((objValue: any, srcValue: any) => {
    if (Array.isArray(srcValue)) {
      return srcValue;
    }
    return undefined;
  });

  const normalizeMomentRange = useMemoizedFn((value: any) => {
    if (!Array.isArray(value)) return value;
    return value.map((item) => (item ? moment(item) : item));
  });

  const formatFilterValuesFromUrl = useMemoizedFn((decoded: any, current: any) => {
    const merged = mergeWith(cloneDeep(current), decoded, overwriteArrayCustomizer);

    const mergedRangeTime = merged?.[filterKeyMap.RANGE_TIME];
    if (mergedRangeTime) {
      merged[filterKeyMap.RANGE_TIME] = {
        ...current?.[filterKeyMap.RANGE_TIME],
        ...mergedRangeTime,
        value: normalizeMomentRange(mergedRangeTime?.value),
      };
    }

    const mergedReleaseTime = merged?.[filterKeyMap.RELEASE_TIME];
    if (mergedReleaseTime) {
      merged[filterKeyMap.RELEASE_TIME] = {
        ...current?.[filterKeyMap.RELEASE_TIME],
        ...mergedReleaseTime,
        value: normalizeMomentRange(mergedReleaseTime?.value),
      };
    }

    const regionMarkets = merged?.[filterKeyMap.REGION]?.markets;
    if (Array.isArray(regionMarkets) && !isEqual(regionMarkets, current?.[filterKeyMap.REGION]?.markets)) {
      userManuallyChangedRegionRef.current = true;
    }

    const mergedPublisherRegion = merged?.[filterKeyMap.PUBLISHER_REGION];
    if (Array.isArray(mergedPublisherRegion?.markets)) {
      merged[filterKeyMap.PUBLISHER_REGION] = {
        ...current?.[filterKeyMap.PUBLISHER_REGION],
        ...mergedPublisherRegion,
        regions: [],
        type: 'market',
      };
    }

    const metricValuesFromUrl = merged?.[filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS]?.value;
    if (Array.isArray(metricValuesFromUrl) && metricValuesFromUrl.length) {
      hasMetricSelectorChangedRef.current = true;
    }

    // Handle Steam Tags logic parameter from URL
    const mergedSteamTags = merged?.[filterKeyMap.STEAM_TAGS];
    if (mergedSteamTags) {
      merged[filterKeyMap.STEAM_TAGS] = {
        ...current?.[filterKeyMap.STEAM_TAGS],
        ...mergedSteamTags,
        logic: mergedSteamTags?.logic || 'AND', // Default to AND for backward compatibility
      };
    }

    return merged;
  });

  const getRadioOptionsTips = useMemoizedFn(() => {
    return { ...configureTexts };
  });

  const filterCfg = useMemo(
    () => ({
      filterKeyList: filterConfigCur?.filterKeyList,
      filterGroups: filterConfigCur?.filterGroups,
      defaultValue: filterConfigCur?.defaultFilterValue,
      resetFilterValue: {
        ...filterConfigCur?.defaultFilterValue,
        [filterKeyMap.RANGE_TIME]: {
          type: rangeTimeDefaultConfig?.type || 'week',
          value: rangeTimeResetValue,
        },
        // 确保 Platform、Steam Tags、Game Mode 筛选器在 Reset 时重置为空
        [filterKeyMap.STEAM_TAGS]: { value: [] },
        [filterKeyMap.GAME_MODE]: { value: [] },
        [filterKeyMap.PLATFORMCASCADER]: { value: {} },
      },
        specialParams: {
          regionOptions,
          steamTagOptions,
          gameModeOptions,
          gameEngineOptions,
          publisherClassOptions,
          defalutMarket: ['global'],
          // 当前所选指标含 Ampere 数据源时启用 'Global except China mainland'，否则禁用
          disableNoChina: !containsAmpere,
          rangeTimeType: FilterTimeType.commonRange,
          disableIegg: false,
          disableAnnie: true,
          genreOptionsLoading: genreOptionsMapLoading,
          genreOptions: genreOptionsMap,
          [filterKeyMap.ALL_Games]: {
            params: { entity_type: entityType, game_type: 'current_game' },
            chooseUpNum: 1000,
          },
          entityType,
          // 热门标签筛选器相关
          hotTagsOptions: hotTagsOptions || [],
          filterTagName,
          hotTagDefaultOption,
          onHotTagConfirm,
          filterTips: getPcConsoleTopChartsFilterTips(formatMessage),
        },
      showConfirmButton: true,
      haveFilterSaveBtn: true,
      hasUrlShortLink,
    }),
    [
      FilterTimeType,
      entityType,
      filterConfigCur?.defaultFilterValue,
      filterConfigCur?.filterKeyList,
      filterConfigCur?.filterGroups,
      filterKeyMap,
      formatMessage,
      gameModeOptions,
      gameEngineOptions,
      publisherClassOptions,
      hasUrlShortLink,
      rangeTimeDefaultConfig?.type,
      rangeTimeResetValue,
      regionOptions,
      steamTagOptions,
      hotTagsOptions,
      filterTagName,
      hotTagDefaultOption,
      onHotTagConfirm,
      genreOptionsMapLoading,
      genreOptionsMap,
      containsAmpere,
    ],
  );

  return {
    filterValue,
    setFilterValue,
    filterPageValue,
    setFilterPageValue,
    filterCfg,
    formatFilterValuesFromUrl,
    getRadioOptionsTips,
    genreOptionsMapLoading,
    formatedGenreOptionsMap,
  };
};

export default usePcConsoleFilters;
